import { Box, Card, Collapse, Stack, Typography } from '@mui/material';
import { format, isPast, isToday } from 'date-fns';
import { FC, useEffect, useState } from 'react';
// import i18n from '../../app/i18n';
import { useTranslation } from 'react-i18next';
import CalendarEvent from './CalendarEvent';

interface CalendarCellsProps {
  day: Date;
  currentMonth: boolean;
  events: Record<string, any>[];
  onEventClick: (event: Record<string, any>) => void;
  onClick: (day: string) => void;
  onClose: () => void;
}

const CalendarCell: FC<CalendarCellsProps> = ({
  day,
  currentMonth,
  events,
  onEventClick,
  onClick,
  onClose,
}) => {
  const { t } = useTranslation();
  const [showMore, setShowMore] = useState(false);
  const [startEvents, setStartEvents] = useState<Record<string, any>[]>([]);
  const [moreEvents, setMoreEvents] = useState<Record<string, any>[]>([]);

  const disabled = isPast(day) && !isToday(day);

  useEffect(() => {
    if (events && events.length > 0) {
      if (events.length > 3) {
        setStartEvents([...events].slice(0, 2));
        setMoreEvents([...events].slice(2));
      } else {
        setStartEvents(events);
      }
    }
  }, [events]);

  const handleAddEvent = () => {
    onClick(format(day, 'yyyy/MM/dd'));
  };

  return (
    <Stack
      component={Card}
      sx={{
        p: { xs: 1, xl: 2 },
        minHeight: 160,
        bgcolor: isPast(day) && !isToday(day) ? '#f5f5f5' : '#fff',
        opacity: isPast(day) && !isToday(day) ? 0.8 : 1,
        position: 'relative',
        pointerEvents: disabled ? 'none' : 'auto',
        cursor: disabled ? 'not-allowed' : 'pointer',
        '&:hover': {
          borderColor: !disabled ? '#0288d1' : '',
          '&:before': {
            display: 'flex',
          },
        },
        '&:before': {
          top: 0,
          left: 0,
          color: '#0288d1',
          right: 0,
          bottom: 0,
          content: '"+"',
          display: 'none',
          position: 'absolute',
          fontSize: 48,
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        },
      }}
      onClick={handleAddEvent}
    >
      <Stack gap={0.5} position="relative" zIndex={1}>
        {startEvents.map((event: any) => (
          <CalendarEvent event={event} key={event.id} onClick={() => onEventClick(event)} />
        ))}
        {moreEvents.length > 0 && (
          <>
            <Collapse orientation="vertical" in={showMore}>
              <Stack gap={0.5} mb={0.5}>
                {moreEvents.map((event: any) => (
                  <CalendarEvent event={event} key={event.id} onClick={() => onEventClick(event)} />
                ))}
              </Stack>
            </Collapse>
            <Box
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                color: 'white',
                bgcolor: 'primary.main',
                alignSelf: 'flex-start',
                cursor: 'pointer',
              }}
              onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                e.stopPropagation();
                setShowMore(!showMore);
              }}
            >
              <Typography variant="caption" component="div">
                {showMore
                  ? t('more.showLess', 'show less')
                  : t('more.count', { count: moreEvents.length })}
              </Typography>
            </Box>
          </>
        )}
      </Stack>

      <Typography
        variant="h4"
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          bottom: 6,
          right: 12,
          opacity: currentMonth ? 1 : 0.3,
        }}
      >
        {day.getDate()}
      </Typography>
    </Stack>
  );
};

export default CalendarCell;
